import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import parse from 'html-react-parser'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import Button from 'components/button/Button'
import { routes } from 'utils/routes'
import { trackEvent } from 'utils/eventTracking'
import { addCashbackPromoCodeToStore } from 'utils/helpers'
import { ReferralProps } from './types'
import ReferralForm from './form/ReferralForm'
import styles from './Referral.module.scss'
import heroImage from '../../../public/images/features/referral/header.jpg'

const Referral: FC<ReferralProps> = ({ referralCode }) => {
  // Redux
  const dispatch = useDispatch()

  // I18N
  const { t } = useTranslation('referral')
  const { locale: language } = useRouter()

  /**
   * Handles the click event on the CTA button
   */
  const handleCtaClick = () => {
    dispatch(showSimulation())

    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started')
  }

  /**
   * Check if referralcode is valid and set cashback promocode (= VIAVIA)
   */
  useEffect(() => {
    if (referralCode && referralCode.valid) {
      addCashbackPromoCodeToStore()
    }
  }, [referralCode, dispatch])

  return (
    <div className={styles.referral}>
      {/* PAGE CONTENT */}
      <section className={classNames('section', styles.hero)}>
        <div className="container has-bg-image">
          <h1 className={styles.title}>
            {referralCode
              ? parse(
                  t('heroTitleReferred', '<span>Dankzij jouw vriend</span><br/>maak jij kans op een e-bike', {
                    name: referralCode.valid === true ? ` ${referralCode.referralCode.referrer.firstName}` : undefined
                  })
                )
              : parse(t('heroTitle', '<span>Overtuig een vriend</span><br/>en win samen 2 e-bikes'))}
          </h1>
        </div>
        <Image
          priority
          className={styles['hero-image']}
          src={heroImage}
          layout="fill"
          objectFit="cover"
          placeholder="blur"
          alt={t('title', 'Overtuig een vriend en win samen 2 e-bikes')}
        />
      </section>

      <section className={styles.cta}>
        <div className="container">
          <div className={styles.row}>
            {referralCode ? (
              <div className={styles.signup}>
                <h2>{t('ctaTitleReferred', 'Registreer je nu bij Bolt')}</h2>
                {referralCode.valid === false && (
                  <div className={styles.feedback}>
                    <div className={styles['error-message']}>
                      <p>
                        {parse(
                          t(
                            'errorInvalidReferralCode',
                            '<strong>De meegestuurde doorverwijzingscode is incorrect.</strong><br />Zorg er voor dat je de juiste doorverwijzingslink gebruikt.'
                          )
                        )}
                      </p>
                    </div>
                  </div>
                )}
                <Button withGradient isFullWidthMobile onClick={() => handleCtaClick()}>
                  {t('ctaButtonReferred', 'Bereken jouw prijs!')}
                </Button>
                {parse(
                  t(
                    'ctaDescriptionReferred',
                    '<p>Door net als jouw vriend energie-afnemer te worden bij Bolt, maak je kans op <strong>een e-bike van het merk Cowboy</strong>. Jullie winnen samen. Dat betekent één e-bike voor jou én één e-bike voor jouw vriend.  2 e-bikes dus! Wij geven bovendien 2 e-bikes weg per 50 deelnemers. Dat is <strong>1 kans op 50 om te winnen</strong>. De moeite!</p>',
                    {
                      name: referralCode.valid === true ? ` ${referralCode.referralCode.referrer.firstName}` : undefined
                    }
                  )
                )}
              </div>
            ) : (
              <ReferralForm />
            )}
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          {!referralCode && (
            <div className={classNames(styles.row, styles['how-it-works'])}>
              <h2>
                <span>{t('howItWorksTitle', 'Hoe werkt het?')}</span>
              </h2>
              <div className={styles.steps}>
                <div className={styles.step}>
                  <h4>{t('howItWorksStep1Title', '1. Genereer je link')}</h4>
                  <p className={styles.description}>
                    {parse(
                      t(
                        'howItWorksStep1Description',
                        "Vul in het veld hierboven je e-mailadres in en klik op de groene knop 'Genereer jouw link'."
                      )
                    )}
                  </p>
                </div>
                <div className={styles.step}>
                  <h4>{t('howItWorksStep2Title', '2. Deel')}</h4>
                  <p className={styles.description}>
                    {parse(
                      t(
                        'howItWorksStep2Description',
                        'Kopieer jouw link, of gebruik de deelknoppen, en deel deze met één of meerdere vrienden. Via deze link moet elke vriend zich registeren bij Bolt.'
                      )
                    )}
                  </p>
                </div>
                <div className={styles.step}>
                  <h4>{t('howItWorksStep3Title', '3. Win')}</h4>
                  <p className={styles.description}>
                    {parse(
                      t(
                        'howItWorksStep3Description',
                        'Wij contacteren jou en je vriend, als jullie gewonnen hebben. Per vriend krijg je 1 kans op 50 om te winnen.'
                      )
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className={styles['extra-info']}>
        <div className="container">
          <div className={classNames(styles.row, styles.persuasion)}>
            <h2>
              <span>{t('persuasionTitle', 'Overtuigingskracht nodig?')}</span>
              <br />
              {!referralCode && t('persuasionTitleExtra', 'Gebruik deze 2 killer argumenten.')}
            </h2>

            <div className={styles.arguments}>
              <Link href={routes(language).projectIceland}>
                <a className={styles.argument}>
                  <h3>{t('persuasion1Title', 'Energie is vervuiler n°1')}</h3>
                  <p className={styles.description}>
                    {parse(
                      t(
                        'persuasion1Description',
                        'Wist je dat de energiesector de grootste vervuiler is wereldwijd? Ook in ons land is bijna alle stroom afkomstig uit fossiele brandstoffen. Dat wil zeggen dat ons energie- of elektriciteitsverbruik rechtstreeks bijdraagt aan de opwarming van de aarde. Denk maar aan alle huishoudtoestellen bij je thuis. <strong>Kiezen voor hernieuwbare energie is de snelste en gemakkelijkste manier om je ecologische voetafdruk te verlagen.</strong>'
                      )
                    )}
                  </p>
                  <Button withGradient isFullWidthMobile>
                    {t('persuasion1Button', 'Lees meer')}
                  </Button>
                </a>
              </Link>
              <Link href={routes(language).pricing}>
                <a className={styles.argument}>
                  <h3>{t('persuasion2Title', 'Groen moet écht groen zijn')}</h3>
                  <p className={styles.description}>
                    {parse(
                      t(
                        'persuasion2Description',
                        '<strong>Als je kiest voor een groen energiecontract, wil je toch zéker zijn dat jouw energieverbruik werkelijk een verschil maakt?</strong> Bolt verbindt energie-afnemer en energie-opwekker op een transparante en persoonlijke manier. Zo weet je waar je energie vandaan komt en waar je geld naartoe gaat. Geen gedoe met certificaten. Geen sjoemelstroom.'
                      )
                    )}
                  </p>
                  <Button withGradient isFullWidthMobile>
                    {t('persuasion2Button', 'Lees meer')}
                  </Button>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Referral
